import { FC, useCallback, useMemo, useState } from 'react';
import { Button, Card, Divider, RadixTable, TColumn } from '@features/ui';
import { StarIcon } from '@features/ui/icons';
import { ApprovePayload, TAwaitingApproval } from '@src/types';
import {
  checkQualifyExpire,
  cn,
  formatCurrency,
  formatDate,
} from '@lib/utils.ts';
import Pagination from '@features/ui/components/pagination.tsx';
import { AccountStatusCell } from '@features/admin/components/AccountStatusCell';

import { AccountChartsDialog } from '../users';

interface PendingPerformanceAccountTableProps {
  data: TAwaitingApproval[];
  currentPage: number;
  totalPages: number;
  onChangePage: (page: number) => void;
  onBlock?: (payload: ApprovePayload) => void;
  onResend?: (payload: ApprovePayload) => void;
}
export const PendingPerformanceAccountTable: FC<
  PendingPerformanceAccountTableProps
> = ({ data, currentPage, totalPages, onChangePage, onResend, onBlock }) => {
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartAccountId, setChartAccountId] = useState<number | null>(null);
  const [chartAccountName, setChartAccountName] = useState<string>('');

  const handleOpenCharts = useCallback((item?: TAwaitingApproval) => {
    if (!item?.id) return;
    setChartAccountId(item.id);
    setChartAccountName(item.rithmic_account_name ?? '');
    setChartsOpen(true);
  }, []);

  const columns = useMemo(
    (): TColumn<TAwaitingApproval>[] => [
      {
        render: (item) => (
          <div className={`pl-3pr-1`}>
            <p className={'text-blue'}>{item?.leeloo_user?.username ?? ''}</p>
            <p>
              {item?.leeloo_user?.first_name ?? ''}{' '}
              {item?.leeloo_user?.last_name ?? ''}
            </p>
          </div>
        ),
        className: 'bg-[#C13839]/20',
        title: <p className={'ml-3'}>User</p>,
      },
      {
        title: 'Account ID/ Package',
        className: 'bg-[#C13839]/20',
        render: (item) => (
          <div className={'pr-1'}>
            <p className={'text-blue'}>{item?.rithmic_account_name ?? ''}</p>
            <p>{item?.product?.title ?? ''}</p>
          </div>
        ),
      },
      {
        title: 'Trading',
        className: 'bg-[#C13839]/20',
        render: (item) => (
          <div className={'pr-1 min-w-[80px]'}>
            <button
              className={`relative flex h-[30px] min-w-[30px] px-1 bg-[#B6D72C] rounded-[2px] items-center w-fit justify-center cursor-pointer hover:bg-[#a0c002] transition-colors`}
              onClick={() => handleOpenCharts(item)}
            >
              {Number(item?.extra_trading_days ?? 0) +
                Number(item?.trading_days ?? 0) >
                Number(item?.trading_days ?? 0) && (
                <StarIcon
                  className="absolute -top-1 -right-1"
                  color="#000000"
                  size={10}
                  title="Has extra trading days"
                />
              )}
              {Number(item?.trading_days) + Number(item?.extra_trading_days)}
            </button>
          </div>
        ),
      },
      {
        title: 'Balance',
        className: 'bg-[#C13839]/20',
        render: (item) => (
          <div className={'pr-1 min-w-[80px]'}>
            <p>${formatCurrency(Number(item?.account_balance ?? 0), 2)}</p>
            <p>
              {item?.account_balance_date
                ? formatDate(item.account_balance_date)
                : ''}
            </p>
          </div>
        ),
      },
      {
        title: 'Status',
        className: 'bg-[#C13839]/20',
        render: (item) => (
          <AccountStatusCell
            qualifyingAccountName={item?.qualifying_account_name}
            qualifyingStatus={item?.qualifying_status}
            qualifyingStatusDetail={item?.qualifying_status_detail}
          />
        ),
      },
      {
        title: 'Expires',
        className: 'bg-[#C13839]/20',
        render: (item) => {
          const expireInfo = checkQualifyExpire(item?.qualify_expire);

          if (!expireInfo) return null;

          return (
            <div className={cn('min-w-[80px] pr-1', expireInfo.cssClass)}>
              <p>{expireInfo.displayText}</p>
              <p>{expireInfo.formattedDate}</p>
            </div>
          );
        },
      },
      {
        title: 'Action',
        className: 'bg-[#C13839]/20',
        render: (item) => (
          <div className={'pr-1 flex gap-x-[15px]'}>
            <Button
              size={'sm'}
              variant={'lime'}
              onClick={() =>
                item?.id &&
                item.leeloo_user &&
                onResend?.({
                  leeloo_account_id: item.id,
                  leeloo_user_id: item.leeloo_user.id,
                })
              }
            >
              Resend
            </Button>
            <Button
              size={'sm'}
              variant={'black'}
              onClick={() =>
                item?.id &&
                item.leeloo_user &&
                onBlock?.({
                  leeloo_account_id: item.id,
                  leeloo_user_id: item.leeloo_user.id,
                })
              }
            >
              Block
            </Button>
          </div>
        ),
      },
    ],
    [onBlock, onResend]
  );

  return (
    <Card className={'flex items-end'}>
      <RadixTable columns={columns} data={data} />
      <Divider className={'w-full mb-7.5'} />
      <Pagination
        current={currentPage}
        total={totalPages}
        onChange={onChangePage}
      />
      <AccountChartsDialog
        accountId={chartAccountId}
        accountName={chartAccountName}
        open={chartsOpen}
        onOpenChange={setChartsOpen}
      />
    </Card>
  );
};
